import React, { useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { StackScreenProps } from '@react-navigation/stack';
import { colors, fontSizes, fontWeights, radius, spacing } from '../theme';
import {
  SERVICES,
  formatDate,
  generateConfirmationCode,
  generateTimeSlots,
  getNextDays,
  getServiceById,
} from '../data/shopData';
import { TimeSlotsSection } from '../components/TimeSlotPicker';
import type { RootStackParamList } from '../navigation/AppNavigator';

type Props = StackScreenProps<RootStackParamList, 'Booking'>;

interface FormValues {
  name: string;
  phone: string;
  email: string;
  year: string;
  make: string;
  model: string;
  color: string;
  damageDescription: string;
}

const DATES = getNextDays(14);

function SectionTitle({ children }: { children: string }) {
  return <Text style={styles.sectionTitle}>{children}</Text>;
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <View style={styles.labelRow}>
      <Text style={styles.label}>{children}</Text>
      {required && <Text style={styles.required}>*</Text>}
    </View>
  );
}

export function BookingScreen({ navigation, route }: Props) {
  const initialServiceId = route.params?.serviceId ?? '';
  const [selectedServiceId, setSelectedServiceId] = useState(initialServiceId);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [form, setForm] = useState<FormValues>({
    name: '',
    phone: '',
    email: '',
    year: '',
    make: '',
    model: '',
    color: '',
    damageDescription: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues | 'service' | 'date' | 'time', string>>>({});

  const timeSlots = selectedDate ? generateTimeSlots(selectedDate) : [];
  const selectedSlot = timeSlots.find(s => s.id === selectedSlotId);

  function updateField(key: keyof FormValues, value: string) {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  }

  function handleDateSelect(date: string) {
    setSelectedDate(date);
    setSelectedSlotId(null);
  }

  function validate(): boolean {
    const newErrors: typeof errors = {};
    if (!selectedServiceId) newErrors.service = 'Please select a service.';
    if (!form.name.trim()) newErrors.name = 'Name is required.';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required.';
    if (!form.email.trim()) newErrors.email = 'Email is required.';
    if (!form.make.trim()) newErrors.make = 'Vehicle make is required.';
    if (!form.model.trim()) newErrors.model = 'Vehicle model is required.';
    if (!selectedDate) newErrors.date = 'Please select a date.';
    if (!selectedSlotId) newErrors.time = 'Please select a time.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const service = getServiceById(selectedServiceId);
    const vehicleParts = [form.year, form.make, form.model, form.color].filter(Boolean);
    const vehicle = vehicleParts.join(' ');

    navigation.navigate('Confirmation', {
      confirmationCode: generateConfirmationCode(),
      serviceId: selectedServiceId,
      date: selectedDate,
      time: selectedSlot?.label ?? '',
      customerName: form.name,
      vehicle: vehicle || `${form.make} ${form.model}`,
    });
  }

  return (
    <SafeAreaView style={styles.safeArea} edges={['bottom']}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* ── Service Selection ──────────────────────────────────── */}
          <View style={styles.section}>
            <SectionTitle>Select a Service</SectionTitle>
            {errors.service && <Text style={styles.errorText}>{errors.service}</Text>}
            <View style={styles.serviceChips}>
              {SERVICES.map(service => {
                const isSelected = service.id === selectedServiceId;
                return (
                  <TouchableOpacity
                    key={service.id}
                    style={[styles.serviceChip, isSelected && styles.serviceChipSelected]}
                    onPress={() => {
                      setSelectedServiceId(service.id);
                      setErrors(prev => ({ ...prev, service: undefined }));
                    }}
                    activeOpacity={0.75}
                  >
                    <Text style={styles.serviceChipIcon}>{service.icon}</Text>
                    <Text style={[styles.serviceChipText, isSelected && styles.serviceChipTextSelected]}>
                      {service.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* ── Contact Info ───────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionTitle>Your Information</SectionTitle>

            <FieldLabel required>Full Name</FieldLabel>
            <TextInput
              style={[styles.input, errors.name ? styles.inputError : null]}
              value={form.name}
              onChangeText={v => updateField('name', v)}
              placeholder="Jane Smith"
              placeholderTextColor={colors.textMuted}
              autoComplete="name"
              returnKeyType="next"
            />
            {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}

            <FieldLabel required>Phone Number</FieldLabel>
            <TextInput
              style={[styles.input, errors.phone ? styles.inputError : null]}
              value={form.phone}
              onChangeText={v => updateField('phone', v)}
              placeholder="(555) 000-0000"
              placeholderTextColor={colors.textMuted}
              keyboardType="phone-pad"
              autoComplete="tel"
              returnKeyType="next"
            />
            {errors.phone && <Text style={styles.errorText}>{errors.phone}</Text>}

            <FieldLabel required>Email Address</FieldLabel>
            <TextInput
              style={[styles.input, errors.email ? styles.inputError : null]}
              value={form.email}
              onChangeText={v => updateField('email', v)}
              placeholder="jane@example.com"
              placeholderTextColor={colors.textMuted}
              keyboardType="email-address"
              autoComplete="email"
              autoCapitalize="none"
              returnKeyType="next"
            />
            {errors.email && <Text style={styles.errorText}>{errors.email}</Text>}
          </View>

          {/* ── Vehicle Info ───────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionTitle>Vehicle Information</SectionTitle>

            <View style={styles.row}>
              <View style={styles.halfField}>
                <FieldLabel>Year</FieldLabel>
                <TextInput
                  style={styles.input}
                  value={form.year}
                  onChangeText={v => updateField('year', v)}
                  placeholder="2022"
                  placeholderTextColor={colors.textMuted}
                  keyboardType="number-pad"
                  maxLength={4}
                />
              </View>
              <View style={styles.halfField}>
                <FieldLabel>Color</FieldLabel>
                <TextInput
                  style={styles.input}
                  value={form.color}
                  onChangeText={v => updateField('color', v)}
                  placeholder="Silver"
                  placeholderTextColor={colors.textMuted}
                />
              </View>
            </View>

            <FieldLabel required>Make</FieldLabel>
            <TextInput
              style={[styles.input, errors.make ? styles.inputError : null]}
              value={form.make}
              onChangeText={v => updateField('make', v)}
              placeholder="Toyota"
              placeholderTextColor={colors.textMuted}
              returnKeyType="next"
            />
            {errors.make && <Text style={styles.errorText}>{errors.make}</Text>}

            <FieldLabel required>Model</FieldLabel>
            <TextInput
              style={[styles.input, errors.model ? styles.inputError : null]}
              value={form.model}
              onChangeText={v => updateField('model', v)}
              placeholder="Camry"
              placeholderTextColor={colors.textMuted}
              returnKeyType="next"
            />
            {errors.model && <Text style={styles.errorText}>{errors.model}</Text>}

            <FieldLabel>Describe the Damage</FieldLabel>
            <TextInput
              style={[styles.input, styles.textarea]}
              value={form.damageDescription}
              onChangeText={v => updateField('damageDescription', v)}
              placeholder="Briefly describe the damage or what you'd like repaired…"
              placeholderTextColor={colors.textMuted}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {/* ── Date Picker ────────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionTitle>Preferred Date</SectionTitle>
            {errors.date && <Text style={styles.errorText}>{errors.date}</Text>}
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.dateRow}
            >
              {DATES.map(date => {
                const isSelected = date === selectedDate;
                const parts = formatDate(date).split(', ');
                const dayName = parts[0];
                const monthDay = parts[1] ?? '';
                return (
                  <TouchableOpacity
                    key={date}
                    style={[styles.dateChip, isSelected && styles.dateChipSelected]}
                    onPress={() => {
                      handleDateSelect(date);
                      setErrors(prev => ({ ...prev, date: undefined }));
                    }}
                    activeOpacity={0.75}
                  >
                    <Text style={[styles.dateDayName, isSelected && styles.dateTextSelected]}>
                      {dayName}
                    </Text>
                    <Text style={[styles.dateMonthDay, isSelected && styles.dateTextSelected]}>
                      {monthDay}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>

          {/* ── Time Picker ────────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionTitle>Preferred Time</SectionTitle>
            {!selectedDate && (
              <Text style={styles.hintText}>Select a date above to see available times.</Text>
            )}
            {errors.time && <Text style={styles.errorText}>{errors.time}</Text>}
            <TimeSlotsSection
              visible={!!selectedDate}
              slots={timeSlots}
              selectedId={selectedSlotId}
              onSelect={id => {
                setSelectedSlotId(id);
                setErrors(prev => ({ ...prev, time: undefined }));
              }}
            />
          </View>

          {/* ── Submit ────────────────────────────────────────────── */}
          <View style={styles.submitSection}>
            <TouchableOpacity
              style={styles.submitBtn}
              onPress={handleSubmit}
              activeOpacity={0.85}
            >
              <Text style={styles.submitBtnText}>Confirm Appointment →</Text>
            </TouchableOpacity>
            <Text style={styles.submitNote}>
              You'll receive a confirmation code after booking. No payment required at this time.
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colors.background,
  },
  flex: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    maxWidth: 680,
    alignSelf: 'center',
    width: '100%',
    paddingBottom: spacing.xxxl,
  },

  // Sections
  section: {
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sectionTitle: {
    fontSize: fontSizes.md,
    fontWeight: fontWeights.semibold,
    color: colors.textPrimary,
    marginBottom: spacing.md,
  },

  // Service chips
  serviceChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  serviceChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  serviceChipSelected: {
    backgroundColor: colors.accentLight,
    borderColor: colors.accent,
  },
  serviceChipIcon: {
    fontSize: 15,
  },
  serviceChipText: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    color: colors.textPrimary,
  },
  serviceChipTextSelected: {
    color: colors.accent,
    fontWeight: fontWeights.semibold,
  },

  // Form fields
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    marginTop: spacing.md,
  },
  label: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    color: colors.textSecondary,
  },
  required: {
    color: colors.error,
    marginLeft: 3,
    fontSize: fontSizes.sm,
  },
  input: {
    backgroundColor: colors.surface,
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    fontSize: fontSizes.base,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  inputError: {
    borderColor: colors.error,
  },
  textarea: {
    minHeight: 90,
    paddingTop: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  halfField: {
    flex: 1,
  },
  errorText: {
    fontSize: fontSizes.xs,
    color: colors.error,
    marginBottom: spacing.xs,
  },
  hintText: {
    fontSize: fontSizes.sm,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },

  // Date picker
  dateRow: {
    paddingVertical: spacing.xs,
    gap: spacing.sm,
  },
  dateChip: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    minWidth: 64,
  },
  dateChipSelected: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  dateDayName: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.semibold,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  dateMonthDay: {
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.bold,
    color: colors.textPrimary,
  },
  dateTextSelected: {
    color: colors.white,
  },

  // Submit
  submitSection: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  submitBtn: {
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
    width: '100%',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  submitBtnText: {
    color: colors.white,
    fontSize: fontSizes.md,
    fontWeight: fontWeights.semibold,
  },
  submitNote: {
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 18,
  },
});
